import { AsyncPipe, isPlatformServer, NgComponentOutlet } from '@angular/common'
import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  DOCUMENT,
  inject,
  OnInit,
  PLATFORM_ID,
  Renderer2,
  TransferState,
  Type,
} from '@angular/core'
import {
  ActivatedRouteSnapshot,
  NavigationEnd,
  RouteConfigLoadEnd,
  RouteConfigLoadStart,
  Router,
  RouterOutlet,
} from '@angular/router'

import { AuthService } from '@auth0/auth0-angular'
import { TranslateService } from '@ngx-translate/core'
import {
  BehaviorSubject,
  exhaustMap,
  filter,
  interval,
  mergeMap,
  of,
  Subscription,
  takeUntil,
  tap,
  withLatestFrom,
} from 'rxjs'

import { DestroyDirective } from '~/core/directives'
import { APP, SIGN_IN_PATH } from '~const/index'
import { ENV } from '~env/environment'

import { FooterComponent, HeaderComponent, ProgressBarComponent } from './core/components'
import {
  APP_DEFAULT_LANGUAGE,
  SEO_NOINDEX_ROUTE_DATA_NAME,
  SEO_PAGE_DATA_NAME,
} from './core/constants'
import {
  CompanyFacade,
  companyStateKey,
  ICompanyState,
  IUserState,
  RouterFacade,
  UiFacade,
  UserFacade,
  userStateKey,
} from './core/features'
import { AdminGuard, AuthGuard } from './core/guards'
import { SeoPage, SeoService } from './core/services'
import { GuestPopupService } from './ui-components/guest-popup'

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  hostDirectives: [DestroyDirective],
  host: { 'data-proximilar-version': ENV.appVersion },
  imports: [
    AsyncPipe,
    RouterOutlet,
    HeaderComponent,
    FooterComponent,
    NgComponentOutlet,
    ProgressBarComponent,
  ],
})
export class AppComponent implements OnInit {
  private readonly document = inject<Document>(DOCUMENT)
  private readonly guestPopupService = inject(GuestPopupService, { optional: true })
  private readonly auth0Service = inject(AuthService, { optional: true })
  private readonly platformID = inject<Object>(PLATFORM_ID)
  private readonly transferState = inject(TransferState)
  private readonly seoService = inject(SeoService)
  private readonly translate = inject(TranslateService)
  private readonly renderer = inject(Renderer2)
  private readonly router = inject(Router)

  private readonly routerFacade = inject(RouterFacade)

  private readonly companyFacade = inject(CompanyFacade)

  private readonly userFacade = inject(UserFacade)

  private readonly destroy$ = inject(DestroyDirective).destroy$
  private guestMessaging$: Subscription | null = null

  uiFacade = inject(UiFacade)

  showLoading$ = new BehaviorSubject(false)

  notificationPopupComponent?: Promise<Type<unknown>>

  constructor() {
    this.translate.setDefaultLang(APP_DEFAULT_LANGUAGE).subscribe()

    afterNextRender(() => {
      this.initGuestListener()
    })

    afterNextRender(() => {
      // this.auth0Service?.initConfig()
      this.userFacade.initAuthUser()

      // this.auth0Service?.isAuthenticated$.pipe(takeUntil(this.destroy$)).subscribe(_isAuthenticated => {})
    })

    afterNextRender(() => {
      this.notificationPopupComponent = import(
        './ui-components/notification-modal/notification-modal.component'
      ).then(c => c.NotificationComponent)
    })
  }

  ngOnInit(): void {
    this.initCompanyStateListener()
    this.initUserStateListener()

    this.uiFacade.init()
    this.userFacade.initAuthUser()
    this.seoService.init(this.router.url, this.isNoindexRoute())
    this.seoService.applyRouteMetadata(this.getRouteSeoPage())
    this.initThemeListener()
    this.initRouteListener()
  }

  private initCompanyStateListener(): void {
    if (isPlatformServer(this.platformID)) {
      this.companyFacade.state$
        .pipe(
          tap(state => this.transferState.set(companyStateKey, state)),
          takeUntil(this.destroy$)
        )
        .subscribe()
    } else {
      const state = this.transferState.get<ICompanyState | null>(companyStateKey, null)

      if (state) {
        this.companyFacade.initState(state)
      }
    }
  }

  private initUserStateListener(): void {
    if (isPlatformServer(this.platformID)) {
      this.userFacade.state$
        .pipe(
          tap(state => this.transferState.set(userStateKey, state)),
          takeUntil(this.destroy$)
        )
        .subscribe()
    } else {
      const state = this.transferState.get<IUserState | null>(userStateKey, null)

      if (state) {
        this.userFacade.initState(state)
      }
    }
  }

  private initThemeListener(): void {
    this.uiFacade.isDarkTheme$
      .pipe(
        tap(isDarkTheme =>
          isDarkTheme
            ? this.renderer.addClass(this.document.body, 'dark-mode')
            : this.renderer.removeClass(this.document.body, 'dark-mode')
        ),
        takeUntil(this.destroy$)
      )
      .subscribe()
  }

  private initRouteListener(): void {
    this.router.events
      .pipe(
        tap(event => {
          if (event instanceof RouteConfigLoadStart) {
            this.uiFacade.startRequest()
          } else if (event instanceof RouteConfigLoadEnd) {
            this.uiFacade.endRequest()
          } else if (event instanceof NavigationEnd) {
            this.seoService.applyRoutePolicy(event.urlAfterRedirects, this.isNoindexRoute())
            this.seoService.applyRouteMetadata(this.getRouteSeoPage())
          }
        }),
        takeUntil(this.destroy$)
      )
      .subscribe()
  }

  private isNoindexRoute(): boolean {
    const activeRoute = this.getActiveRoute()

    return activeRoute.pathFromRoot.some(route => {
      const hasPrivateGuard = route.routeConfig?.canActivate?.some(
        guard => guard === AuthGuard || guard === AdminGuard
      )

      return hasPrivateGuard || route.data[SEO_NOINDEX_ROUTE_DATA_NAME] === true
    })
  }

  private getRouteSeoPage(): SeoPage | undefined {
    const activeRoute = this.getActiveRoute()

    return [...activeRoute.pathFromRoot]
      .reverse()
      .map(route => route.data[SEO_PAGE_DATA_NAME] as SeoPage | undefined)
      .find(page => page !== undefined)
  }

  private getActiveRoute(): ActivatedRouteSnapshot {
    let activeRoute = this.router.routerState.snapshot.root

    while (activeRoute.firstChild) {
      activeRoute = activeRoute.firstChild
    }

    return activeRoute
  }

  private initGuestListener(): void {
    this.userFacade.user$
      .pipe(
        tap(u => (u === null ? this.runGuestMessaging() : this.stopGuestMessaging())),
        takeUntil(this.destroy$)
      )
      .subscribe()
  }

  private runGuestMessaging(): void {
    this.stopGuestMessaging()

    this.guestMessaging$ = interval(APP.GUEST_DURATION * 1000)
      .pipe(
        withLatestFrom(this.userFacade.user$, this.routerFacade.isCompanyPage$),
        filter(([, user, isCompanyPage]) => !user && isCompanyPage),
        exhaustMap(() => this.guestPopupService?.openDialog() || of()),
        mergeMap(login => (login ? this.router.navigateByUrl(`/${SIGN_IN_PATH}`) : of(false))),
        tap(() => this.runGuestMessaging()), // reset the interval after close the popup
        takeUntil(this.destroy$)
      )
      .subscribe()
  }

  private stopGuestMessaging(): void {
    if (this.guestMessaging$) {
      this.guestMessaging$.unsubscribe()
      this.guestMessaging$ = null
    }
  }
}
